import { z } from 'zod'

/** Fases do protocolo EMDR. No protótipo só carregamos o número. */
export const phaseSchema = z.number().int().min(1).max(8)

/**
 * Frequência da estimulação bilateral, em Hz. Um Hz é um vai-e-volta completo,
 * ou seja dois passes. A faixa clínica usual fica entre 0,5 e 2 Hz.
 */
export const hzSchema = z.number().min(0.2).max(3)

export const passesPerSetSchema = z.number().int().min(2).max(60)

export const roleSchema = z.enum(['therapist', 'patient'])

export const roomIdSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-zA-Z0-9_-]+$/, 'id da sala aceita só letras, números, hífen e underline')

/** Id da sessão marcada, o mesmo uuid de `scheduled_session`. */
export const sessionIdSchema = z.uuid()

/** Entrada normal: quem é a pessoa sai da linha da sessão, nunca do payload. */
export const sessionJoinSchema = z.object({
  sessionId: sessionIdSchema,
})

/**
 * Entrada sem sessão marcada, com papel declarado pelo cliente. Só vale com
 * `config.room.allowAnonymous`, que existe para a página de medição e para a
 * spec de deriva continuarem entrando sozinhas fora de produção.
 */
export const anonymousJoinSchema = z.object({
  roomId: roomIdSchema,
  role: roleSchema,
})

export const joinSchema = z.union([sessionJoinSchema, anonymousJoinSchema])

export const sessionStateSchema = z.object({
  phase: phaseSchema,
  /** Instante do começo da série no relógio do servidor, em ms. */
  setStartAt: z.number().nullable(),
  hz: hzSchema,
  passesPerSet: passesPerSetSchema,
  running: z.boolean(),
  /** Séries concluídas desde que a sala abriu. */
  setsCompleted: z.number().int().min(0),
  /** Passes da série em andamento. Zera a cada série. */
  passesInSet: z.number().int().min(0),
})

export const commandSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('start-set') }),
  z.object({ type: z.literal('stop') }),
  z.object({ type: z.literal('set-hz'), hz: hzSchema }),
  z.object({ type: z.literal('set-passes'), passesPerSet: passesPerSetSchema }),
  z.object({ type: z.literal('set-phase'), phase: phaseSchema }),
])

export const setCompleteSchema = z.object({
  setIndex: z.number().int().min(1),
  passes: z.number().int().min(0),
  startedAt: z.number(),
  endedAt: z.number(),
})

export const heartbeatSchema = z.object({
  /** Relógio do servidor no momento do envio, em ms. */
  at: z.number(),
})

/**
 * Envelope cifrado atravessando o socket.
 *
 * O servidor não abre nem sabe abrir: valida só o formato e carrega. A
 * implementação de verdade mora em `@bia/crypto`, e o protocolo não depende
 * dela de propósito, para a sala continuar sem dependência além do zod.
 */
export const sealedEnvelopeSchema = z.object({
  v: z.literal(1),
  alg: z.literal('A256GCM'),
  /** Identificador da chave que abre este envelope. */
  kid: z.string().min(1).max(128),
  /** Nonce do AES-GCM, base64url. */
  iv: z.string().min(1).max(128),
  /** Ciphertext com tag, base64url. */
  ct: z.string().min(1).max(200_000),
  /** Dado autenticado, no formato `tabela.coluna:rowId`. */
  aad: z.string().min(1).max(256),
})

/**
 * Caixa selada do paciente para a terapeuta. Opaca aqui: o servidor só
 * confere que é um objeto e repassa. Quem valida o formato inteiro é o
 * CHECK da coluna, na hora de gravar.
 */
export const opaqueSealedSchema = z.looseObject({ v: z.literal(1) })

export const measureKindSchema = z.enum(['sud', 'voc'])

/**
 * SUD ou VOC já cifrado no navegador da terapeuta. O valor não entra no
 * `sessionStateSchema`: ele não é estado da sala, é registro clínico.
 */
export const measureEventSchema = z.object({
  kind: measureKindSchema,
  envelope: sealedEnvelopeSchema,
  /** Instante no relógio do servidor, em ms. */
  at: z.number(),
})

/** Nota rápida da terapeuta durante a sessão. Não volta para o paciente. */
export const quickNoteSchema = z.object({
  envelope: sealedEnvelopeSchema,
  at: z.number(),
})

/** Check-in do paciente, em caixa selada. Só o navegador dela abre. */
export const checkinSchema = z.object({
  sealed: opaqueSealedSchema,
  at: z.number(),
})

/** Sala de reserva quando o vídeo não fecha. Só a terapeuta manda o link. */
export const planBSchema = z.object({
  link: z.url().max(2000),
})

export type Phase = z.infer<typeof phaseSchema>
export type Role = z.infer<typeof roleSchema>
export type SessionJoin = z.infer<typeof sessionJoinSchema>
export type AnonymousJoin = z.infer<typeof anonymousJoinSchema>
export type Join = z.infer<typeof joinSchema>
export type SessionState = z.infer<typeof sessionStateSchema>
export type Command = z.infer<typeof commandSchema>
export type SetComplete = z.infer<typeof setCompleteSchema>
export type Heartbeat = z.infer<typeof heartbeatSchema>
export type SealedEnvelope = z.infer<typeof sealedEnvelopeSchema>
export type OpaqueSealed = z.infer<typeof opaqueSealedSchema>
export type MeasureKind = z.infer<typeof measureKindSchema>
export type MeasureEvent = z.infer<typeof measureEventSchema>
export type QuickNote = z.infer<typeof quickNoteSchema>
export type Checkin = z.infer<typeof checkinSchema>
export type PlanB = z.infer<typeof planBSchema>
